import React, { ReactNode } from 'react';
import moment from 'moment';
import { unparse as convertToCSV } from 'papaparse/papaparse.min';
import {
    downloadCSV,
    TextField,
    DateField,
    ShowButton,
    Filter,
    ReferenceInput,
    SelectInput,
    AutocompleteInput,
    DateInput,
    Link,
    FunctionField,
} from 'react-admin';
import CustomizableGrid from '../components/CustomizableGrid';
import { ColumnConfig } from '../hooks/useColumnReorder';
import { makeStyles } from '@material-ui/core/styles';
import ChipField from '../components/ChipField';
import BoxitChipField from '../components/BoxitChipField';
import MoneyField from '../components/MoneyField';
import BoxitPagination from '../components/BoxitPagination';
import UserReferenceField from '../components/UserReferenceField';
import { PaymentStatus, PaymentType, PaymentMode } from '../constants';
import CountryField from '../components/CountryField';
import { formatMoney } from '../util/formatMoney';
import { SelectArrayInput } from 'react-admin';

const useStyles = makeStyles({
    root: {
        padding: '1px 8px',
        color: '#595959',
        background: '#F5F5F5',
        borderRadius: '4px',
    },
});
const PaymentTypeField = ({ record = {}, source }) => {
    const classes = useStyles();
    return record && record[source] && record[source] !== 'payment' ? (
        <ChipField className={classes.root} record={record} source={source} />
    ) : null;
};

const exporter = (records, fetchRelatedRecords) => {
    fetchRelatedRecords(records, 'countryId', 'country').then(countries => {
        const data = records.map(record => ({
            ...record,
            amount: formatMoney(record.amount, record.currency),
            country:
                countries[record.countryId] && countries[record.countryId].name,
            name: record.user.name,
        }));
        const csv = convertToCSV({
            data,
            fields: [
                'id',
                'type',
                'mode',
                'currency',
                'amount',
                'status',
                'paidAt',
                'name',
                'country',
            ],
        });
        downloadCSV(csv, `Payments_${Date.now()}`);
    });
};

const PaymentFilter = props => (
    <Filter variant="outlined" {...props}>
        <ReferenceInput
            source="partnerId"
            reference="partners"
            resource="partners"
            label="Partner"
        >
            <SelectInput />
        </ReferenceInput>

        <SelectInput
            source="type"
            choices={Object.keys(PaymentType).map(c => ({ id: c, name: c }))}
        />

        <SelectArrayInput
            style={{ minWidth: '10rem' }}
            source="status"
            choices={Object.keys(PaymentStatus).map(c => ({ id: c, name: c }))}
        />

        <SelectInput
            source="mode"
            choices={Object.keys(PaymentMode).map(c => ({ id: c, name: c }))}
        />

        <SelectInput
            label="Method"
            source="data.response.eci"
            choices={[
                { id: 'ECOMMERCE', name: 'ECOMMERCE' },
                { id: 'RECURRING', name: 'RECURRING' },
            ]}
        />

        <ReferenceInput
            source="userId"
            reference="users"
            resource="users"
            label="Customer"
        >
            <AutocompleteInput
                shouldRenderSuggestions={val => val.trim().length > 2}
                optionText={user =>
                    user && (user && user.name ? user.name : user.email)
                }
            />
        </ReferenceInput>

        <SelectInput
            source="countryId"
            label="Country"
            choices={[
                { id: 3, name: 'KSA' },
                { id: 1, name: 'UAE' },
                { id: 2, name: 'Kuwait' },
            ]}
        />

        <DateInput
            source="paidAt[$gte]"
            label="Date From"
            parse={v => v && moment(v).startOf('day').format()}
        />
        <DateInput
            source="paidAt[$lte]"
            label="Date To"
            parse={v => v && moment(v).endOf('day').format()}
            format={v =>
                v && moment(v).subtract(1, 'days').format('YYYY-MM-DD')
            }
        />
    </Filter>
);

// Custom field components with links
const CustomerNameField = ({ record }) => {
    if (!record || !record.user || !record.user.id || !record.user.name)
        return null;
    return <Link to={`/users/${record.user.id}/show`}>{record.user.name}</Link>;
};

const PartnerField = ({ record }) => {
    if (
        !record ||
        !record.partner ||
        !record.partner.id ||
        !record.partner.name
    )
        return null;
    return (
        <Link to={`/partners/${record.partner.id}/show`}>
            {record.partner.name}
        </Link>
    );
};

const CreatorField = ({ record }) => {
    if (
        !record ||
        !record.creator ||
        !record.creator.id ||
        !record.creator.name
    )
        return null;
    return (
        <Link to={`/users/${record.creator.id}/show`}>
            {record.creator.name}
        </Link>
    );
};

const CountryNameField = ({ record }) => {
    if (!record || !record.country || !record.country.name) return null;
    return <span>{record.country.name}</span>;
};

const PaymentList = ({ permissions = [], ...props }) => {
    const classes = useStyles();

    // Define default columns configuration
    const defaultColumns: ColumnConfig[] = [
        { id: 'serial', label: 'Serial', visible: true, order: 0 },
        { id: 'id', label: 'ID', visible: true, order: 1 },
        { id: 'createdAt', label: 'Created', visible: true, order: 2 },
        { id: 'customer', label: 'Customer', visible: true, order: 3 },
        { id: 'partner', label: 'Partner', visible: true, order: 4 },
        { id: 'country', label: 'Country', visible: true, order: 5 },
        { id: 'mode', label: 'Mode', visible: true, order: 6 },
        { id: 'method', label: 'Method', visible: true, order: 7 },
        { id: 'amount', label: 'Amount', visible: true, order: 8 },
        { id: 'type', label: 'Type', visible: true, order: 9 },
        { id: 'status', label: 'Status', visible: true, order: 10 },
        { id: 'paidAt', label: 'Date', visible: true, order: 11 },
        { id: 'creator', label: 'Creator', visible: true, order: 12 },
        { id: 'show', label: 'Show', visible: true, order: 13 },
    ];

    // Function to render column based on column ID
    const renderColumn = (columnId: string): ReactNode => {
        switch (columnId) {
            case 'serial':
                return (
                    <TextField
                        key={columnId}
                        source="serial"
                        sortable={false}
                    />
                );
            case 'id':
                return <TextField key={columnId} source="id" />;
            case 'createdAt':
                return (
                    <DateField
                        key={columnId}
                        locales="en-IN"
                        source="createdAt"
                        label="Created"
                        showTime
                    />
                );
            case 'customer':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer"
                        render={record => <CustomerNameField record={record} />}
                    />
                );
            case 'partner':
                return (
                    <FunctionField
                        key={columnId}
                        label="Partner"
                        render={record => <PartnerField record={record} />}
                    />
                );
            case 'country':
                return (
                    <FunctionField
                        key={columnId}
                        label="Country"
                        render={record => <CountryNameField record={record} />}
                    />
                );
            case 'mode':
                return (
                    <ChipField
                        key={columnId}
                        className={classes.root}
                        source="mode"
                    />
                );
            case 'method':
                return (
                    <ChipField
                        key={columnId}
                        source="data.response.eci"
                        label="Method"
                    />
                );
            case 'amount':
                return (
                    <MoneyField key={columnId} source="amount" showCurrency />
                );
            case 'type':
                return <PaymentTypeField key={columnId} source="type" />;
            case 'status':
                return (
                    <BoxitChipField
                        key={columnId}
                        record
                        source="status"
                        list="payment"
                    />
                );
            case 'paidAt':
                return (
                    <DateField
                        key={columnId}
                        locales="en-IN"
                        source="paidAt"
                        label="Date"
                    />
                );
            case 'creator':
                return (
                    <FunctionField
                        key={columnId}
                        label="Creator"
                        render={record => <CreatorField record={record} />}
                    />
                );
            case 'show':
                return <ShowButton key={columnId} />;
            default:
                return null;
        }
    };

    // Ensure we have all required props, but avoid duplicates with what might be in props
    const combinedProps = {
        title: 'Payments',
        permissions,
        defaultColumns,
        storageKey: 'paymentListColumns',
        renderColumn,
        bulkActionButtons: false,
        exporter: exporter,
        filtersComponent: <PaymentFilter />,
        sort: { field: 'createdAt', order: 'DESC' },
        paginationComponent: <BoxitPagination />,
        ...props,
        // Ensure these are set if not in props
        resource: props.resource || 'payments',
        location: props.location || { search: '' },
        hasShow: props.hasShow !== undefined ? props.hasShow : true,
    };

    return <CustomizableGrid {...combinedProps} />;
};

PaymentList.defaultProps = {
    resource: 'payments',
    location: { search: '' },
};

export default PaymentList;
